import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/apiClient";
import PermissionGate from "@/components/PermissionGate";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Layers, Plus, Pencil, Trash2 } from "lucide-react";

const EMPTY = { name: "", is_active: true };

export default function CategoriesTab({ onChange }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/categories");
    setRows(data || []);
    onChange?.();
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (c) => { setForm({ name: c.name, is_active: c.is_active }); setEditId(c.id); setOpen(true); };

  const save = async () => {
    setSaving(true);
    try {
      if (editId) await api.put(`/categories/${editId}`, form);
      else await api.post("/categories", form);
      toast.success(editId ? "Category updated" : "Category created");
      setOpen(false); await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete category "${c.name}"?`)) return;
    try { await api.delete(`/categories/${c.id}`); toast.success("Category deleted"); await load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="categories-tab">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h3 className="font-display text-lg font-semibold text-secondary">Course Categories</h3>
          <p className="text-sm text-muted-foreground">Group courses into categories.</p>
        </div>
        <PermissionGate permission="category.create">
          <Button onClick={openNew} data-testid="add-category-button"><Plus className="mr-1.5 h-4 w-4" /> Add category</Button>
        </PermissionGate>
      </div>

      <div className="rounded-xl border border-border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead><TableHead>Courses</TableHead>
              <TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((c) => (
              <TableRow key={c.id} data-testid={`category-row-${c.slug}`}>
                <TableCell className="flex items-center gap-2 font-medium text-secondary"><Layers className="h-4 w-4 text-primary" />{c.name}</TableCell>
                <TableCell>{c.course_count}</TableCell>
                <TableCell><Badge className={c.is_active ? "border-0 bg-accent/10 text-accent" : "border-0 bg-slate-100 text-slate-600"}>{c.is_active ? "Active" : "Inactive"}</Badge></TableCell>
                <TableCell className="text-right">
                  <PermissionGate permission="category.update">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(c)} data-testid={`edit-category-${c.slug}`}><Pencil className="h-4 w-4" /></Button>
                  </PermissionGate>
                  <PermissionGate permission="category.delete">
                    <Button variant="ghost" size="sm" className="text-destructive" onClick={() => remove(c)} data-testid={`delete-category-${c.slug}`}><Trash2 className="h-4 w-4" /></Button>
                  </PermissionGate>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="category-dialog">
          <DialogHeader><DialogTitle>{editId ? "Edit category" : "Add category"}</DialogTitle>
            <DialogDescription>Name the category and set its active status.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="category-name-input" /></div>
            <div className="flex items-center gap-2"><Switch checked={form.is_active} onCheckedChange={(v) => setForm((f) => ({ ...f, is_active: v }))} data-testid="category-active-switch" /><Label>Active</Label></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="save-category-button">{saving ? "Saving..." : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
